import "./Dashboard.css";
import { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import core from "../../core";
import useT from "../../i18n/useT";
import { CarFrontFill, Dropbox, FileEarmarkText, ChatDotsFill, BoxSeam, CashStack, ExclamationTriangleFill, People, GraphUpArrow, Wallet2, Bank, ShieldExclamation } from "react-bootstrap-icons";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n) || 0);
const statusPalette = ["#727cf5", "#34c38f", "#f7b84b", "#f1556c", "#4aa3ff", "#8b5cf6", "#0ac6d8", "#6c757d"];

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const user = useSelector((state) => state.user);
    const t = useT();
    const history = useHistory();
    const go = (route) => () => { if (route) history.push(route); };

    useEffect(() => {
        axios.get("/api/dashboard-stats")
            .then((res) => { setStats(res.data.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const kpis = stats ? [
        { key: "vehicles", label: t("dashboard.vehicles"), value: stats.vehicles, icon: <CarFrontFill />, tone: "indigo", route: "/cars" },
        { key: "containers", label: t("dashboard.containers"), value: stats.containers, icon: <BoxSeam />, tone: "teal", route: "/containers" },
        { key: "bookings", label: t("dashboard.bookings"), value: stats.bookings, icon: <FileEarmarkText />, tone: "amber", route: "/booking" },
        { key: "boats", label: t("dashboard.boats"), value: stats.boats, icon: <Dropbox />, tone: "blue", route: "/boats" },
        stats.canSeeUsers
            ? { key: "users", label: t("dashboard.users"), value: stats.users, icon: <People />, tone: "violet", route: "/users" }
            : { key: "tickets", label: t("dashboard.openTickets"), value: stats.openTickets, icon: <ChatDotsFill />, tone: "violet", route: "/ticket" },
    ] : [];

    const totalStatus = stats ? stats.statusBreakdown.reduce((s, x) => s + Number(x.count), 0) || 1 : 1;
    const paidRatio = stats ? (Number(stats.totalPaid) / ((Number(stats.totalPaid) + Number(stats.totalDebt)) || 1)) * 100 : 0;

    return (
        <div className="dash">
            <div className="dash-head">
                <div>
                    <h2 className="dash-title">{t("dashboard.title")}</h2>
                    <p className="dash-sub">{t("dashboard.welcome")}{user.name ? `, ${user.name}` : ""} 👋</p>
                </div>
            </div>

            {loading && <div className="dash-loading"><div className="loader" role=""></div></div>}

            {!loading && stats && <>
                <div className="dash-kpis">
                    {kpis.map((k) => (
                        <div key={k.key} className="kpi-card kpi-clickable" onClick={go(k.route)} role="button" tabIndex={0}>
                            <div className={`kpi-icon kpi-${k.tone}`}>{k.icon}</div>
                            <div className="kpi-meta">
                                <span className="kpi-value">{k.value}</span>
                                <span className="kpi-label">{k.label}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {stats.finance && (
                    <div className="dash-kpis">
                        <div className="kpi-card kpi-clickable" onClick={go("/earnings")} role="button" tabIndex={0}><div className="kpi-icon kpi-teal"><GraphUpArrow /></div><div className="kpi-meta"><span className="kpi-value">{money(stats.finance.incomeThisMonth)}</span><span className="kpi-label">{t("dashboard.incomeThisMonth")}</span></div></div>
                        <div className="kpi-card kpi-clickable" onClick={go("/balance-requests")} role="button" tabIndex={0}><div className="kpi-icon kpi-amber"><Wallet2 /></div><div className="kpi-meta"><span className="kpi-value">{stats.finance.pendingBalanceRequests}</span><span className="kpi-label">{t("dashboard.pendingBalance")}</span></div></div>
                        <div className="kpi-card kpi-clickable" onClick={go("/financing")} role="button" tabIndex={0}><div className="kpi-icon kpi-blue"><Bank /></div><div className="kpi-meta"><span className="kpi-value">{money(stats.finance.financingOutstanding)}</span><span className="kpi-label">{t("dashboard.financingOutstanding")}</span></div></div>
                        <div className="kpi-card kpi-clickable" onClick={go("/reports")} role="button" tabIndex={0}><div className="kpi-icon kpi-violet"><ShieldExclamation /></div><div className="kpi-meta"><span className="kpi-value">{money(stats.finance.customsFinesAccrued)}</span><span className="kpi-label">{t("dashboard.customsFines")}</span></div></div>
                    </div>
                )}

                <div className="dash-grid">
                    <div className="dash-card finance-card kpi-clickable" onClick={go("/transactions")} role="button" tabIndex={0}>
                        <h3 className="dash-card-title"><CashStack /> {t("dashboard.finance")}</h3>
                        <div className="finance-figures">
                            <div className="finance-item">
                                <span className="finance-label">{t("dashboard.outstandingDebt")}</span>
                                <span className="finance-value debt">{money(stats.totalDebt)}</span>
                            </div>
                            <div className="finance-item">
                                <span className="finance-label">{t("dashboard.collected")}</span>
                                <span className="finance-value paid">{money(stats.totalPaid)}</span>
                            </div>
                        </div>
                        <div className="finance-bar">
                            <div className="finance-bar-fill" style={{ width: `${paidRatio}%` }}></div>
                        </div>
                        <div className="finance-foot">
                            <span><ExclamationTriangleFill className="warn" /> {stats.unpaidVehicles} {t("dashboard.unpaidVehicles")}</span>
                            <span>{paidRatio.toFixed(1)}% {t("dashboard.percentCollected")}</span>
                        </div>
                    </div>

                    <div className="dash-card status-card">
                        <h3 className="dash-card-title">{t("dashboard.vehiclesByStatus")}</h3>
                        {stats.statusBreakdown.length === 0 && <p className="dash-empty">{t("table.noData")}</p>}
                        <div className="status-list">
                            {stats.statusBreakdown.map((s, i) => (
                                <div key={i} className="status-row">
                                    <div className="status-row-head">
                                        <span className="status-name">{s.status || t("table.notDefined")}</span>
                                        <span className="status-count">{s.count}</span>
                                    </div>
                                    <div className="status-track">
                                        <div className="status-fill" style={{ width: `${(Number(s.count) / totalStatus) * 100}%`, background: statusPalette[i % statusPalette.length] }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="dash-card recent-card">
                    <h3 className="dash-card-title">{t("dashboard.recentVehicles")}</h3>
                    <div className="recent-table-wrap">
                        <table className="recent-table">
                            <thead>
                                <tr><th>{t("dashboard.thVehicle")}</th><th>{t("dashboard.thVin")}</th><th>{t("dashboard.thStatus")}</th><th>{t("dashboard.thAdded")}</th></tr>
                            </thead>
                            <tbody>
                                {stats.recentVehicles.map((v) => (
                                    <tr key={v.id}>
                                        <td className="rv-name">{v.mark} {v.model} <span className="rv-year">{v.year}</span></td>
                                        <td className="rv-vin">{v.vin}</td>
                                        <td><span className="rv-status" style={{ background: `${v.status_color || "#727cf5"}22`, color: v.status_color || "#727cf5" }}>{v.current_status || "—"}</span></td>
                                        <td className="rv-date">{v.create_date ? core.formdatDate(v.create_date, "yyyy-mm-dd") : "—"}</td>
                                    </tr>
                                ))}
                                {stats.recentVehicles.length === 0 && <tr><td colSpan={4} className="dash-empty">{t("dashboard.noVehicles")}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>}
        </div>
    );
};

export default Dashboard;
